import {
  type DragEvent,
  JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { startBuilderVisit } from "src/telemetry";
import { Modal } from "react-bootstrap";
import {
  GridLayout,
  getBreakpointFromWidth,
  Responsive as ResponsiveGridLayout,
  type Layout,
  type LayoutItem,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout";

import Sidebar, {
  BUILDER_WIDGET_DRAG_TYPE,
  BUILDER_WIDGET_TEMPLATES,
  type BuilderCardTemplate,
  type BuilderWidgetTemplate,
} from "src/components/builder/Sidebar";
import {
  deleteDashboardWidgetApi,
  getDashboardsApi,
  getDashboardTemplatesApi,
  saveAsDashboardApi,
  updateDashboardLayoutApi,
} from "src/api/dashboardApi";
import { usePortalMe } from "src/hooks/usePortal";
import { usePortalAuthStore } from "src/store/portalAuthStore";
import { useAuthStore } from "src/store/authStore";
import type { DashboardListResponse } from "src/types/dashboard";
import Slot from "src/components/builder/Slot";
import SubHeader from "src/components/builder/SubHeader";
import CreateDashboardModal from "src/components/modals/CreateDashboardModal";
import {
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_MARGINS,
  GRID_MIN_WIDTHS,
  GRID_ROW_HEIGHTS,
  GRID_CONTAINER_PADDING,
  GRID_MARGIN,
} from "src/constants/dashboard";
import useBuilderStore from "src/store/builderStore";
import { toConfigOverride } from "src/charts/widgetConfig";
import type {
  DashboardTemplateDefinition,
  DashboardTemplatesResponse,
  DashboardTemplateTierGroup,
} from "src/types/dashboard";
import { sortLayoutItems } from "src/helpers/builderLayout";
import { buildResponsiveLayoutsFromLayout } from "src/helpers/Dashboard";
import useBuilderGridInteractions from "src/hooks/useBuilderGridInteractions";
export const BUILDER_GRID_COLS = GRID_COLS.lg;
export const BUILDER_ROW_HEIGHT = 72;
export const FREE_STYLE_LAYOUT_KEY = "free_style";

export const DEFAULT_WIDGET_TEMPLATES_BY_TYPE = new Map(
  BUILDER_WIDGET_TEMPLATES.map((widget) => [widget.chartType, widget]),
);

type BuilderWidgetGridConfig = {
  defaultGridW: number;
  defaultGridH: number;
  minGridW: number;
  minGridH: number;
  maxGridW: number;
  maxGridH: number;
};

const DEFAULT_WIDGET_GRID_CONFIG: BuilderWidgetGridConfig = {
  defaultGridW: 12,
  defaultGridH: 6,
  minGridW: 2,
  minGridH: 2,
  maxGridW: 24,
  maxGridH: 24,
};

const CHART_TYPE_WIDGET_GRID_CONFIG: Record<string, BuilderWidgetGridConfig> = {
  kpi: {
    defaultGridW: 12,
    defaultGridH: 2,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 24,
    maxGridH: 24,
  },
  kpi_with_detail: {
    defaultGridW: 12,
    defaultGridH: 2,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 24,
    maxGridH: 24,
  },
  multi_line: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 24,
    maxGridH: 24,
  },
  pie: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  pie_drilldown: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  data_table: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  map: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  force_bar: {
    defaultGridW: 12,
    defaultGridH: 8,
    minGridW: 6,
    minGridH: 6,
    maxGridW: 24,
    maxGridH: 24,
  },
  stacked_bar: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  time_series_bar: {
    defaultGridW: 12,
    defaultGridH: 6,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  funnel: {
    defaultGridW: 12,
    defaultGridH: 8,
    minGridW: 4,
    minGridH: 4,
    maxGridW: 24,
    maxGridH: 24,
  },
  quadrant_table: {
    defaultGridW: 12,
    defaultGridH: 8,
    minGridW: 4,
    minGridH: 6,
    maxGridW: 24,
    maxGridH: 24,
  },
  special_quadrant_chart: {
    defaultGridW: 24,
    defaultGridH: 10,
    minGridW: 24,
    minGridH: 10,
    maxGridW: 24,
    maxGridH: 10,
  },
  special_multi_line: {
    defaultGridW: 12,
    defaultGridH: 8,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 24,
    maxGridH: 24,
  },
  bubble_quadrant_chart: {
    defaultGridW: 12,
    defaultGridH: 9,
    minGridW: 12,
    minGridH: 8,
    maxGridW: 24,
    maxGridH: 24,
  },
};

const getWidgetGridConfigByChartType = (
  chartType: string,
): BuilderWidgetGridConfig => {
  const chartTypeGridConfig = CHART_TYPE_WIDGET_GRID_CONFIG[chartType];

  return {
    ...DEFAULT_WIDGET_GRID_CONFIG,
    ...chartTypeGridConfig,
    defaultGridW: DEFAULT_WIDGET_GRID_CONFIG.defaultGridW,
  };
};

export const normalizeDashboardTemplate = (
  template: DashboardTemplateDefinition,
  tierName: string,
) => {
  const fallbackTemplate = DEFAULT_WIDGET_TEMPLATES_BY_TYPE.get(
    template.chart_type,
  );
  const gridConfig = getWidgetGridConfigByChartType(template.chart_type);
  const normalizedDescription =
    (template.description ?? "").trim() ||
    fallbackTemplate?.description ||
    (template.chart_type === "kpi"
      ? "Surface a single KPI value for quick operational tracking."
      : "No description available for this widget.");

  return {
    id: String(template.id),
    templateId: template.id,
    code: template.code,
    title: template.title,
    chartType: template.chart_type,
    defaultGridW: gridConfig.defaultGridW,
    defaultGridH: gridConfig.defaultGridH,
    minGridW: gridConfig.minGridW,
    minGridH: gridConfig.minGridH,
    maxGridW: gridConfig.maxGridW,
    maxGridH: gridConfig.maxGridH,
    category: tierName,
    description: normalizedDescription,
    icon: fallbackTemplate?.icon ?? "bi-grid-1x2",
    // Sidebar's accordion groups by `group` — the tier name, now that
    // GET /widget_templates groups server-side by tier (confirmed live: the
    // old `template.group` field, e.g. "foundation"/"insight", is a separate,
    // unrelated display label and no longer drives this UI).
    group: tierName,
    // Which dashboards already use this widget. Without this the sidebar
    // badge has no data: the normalizer rebuilds the template field by field,
    // so anything not copied here is dropped.
    dashboards: template.dashboards ?? [],
  };
};

export const flattenDashboardTemplates = (
  tiers: DashboardTemplateTierGroup[],
) =>
  tiers.flatMap((tier) =>
    (tier.widgets ?? [])
      .filter((template) => template.is_active !== false)
      .map((template) => normalizeDashboardTemplate(template, tier.tier_name)),
  );

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const getWidgetDataTemplateId = (value: unknown): string | null => {
  if (!isRecord(value)) {
    return null;
  }

  const templateId =
    value.widget_template_id ??
    value.template_id ??
    value.widget_id ??
    value.id;

  if (typeof templateId === "string" || typeof templateId === "number") {
    return String(templateId);
  }

  return null;
};

export const normalizeWidgetDataCollection = (
  collection: unknown[],
): Record<string, unknown> =>
  collection.reduce<Record<string, unknown>>((accumulator, item) => {
    const templateId = getWidgetDataTemplateId(item);

    if (!templateId || !isRecord(item)) {
      return accumulator;
    }

    accumulator[templateId] = Object.prototype.hasOwnProperty.call(item, "data")
      ? item.data
      : item;

    return accumulator;
  }, {});

export const normalizeWidgetDataRecord = (
  collection: Record<string, unknown>,
): Record<string, unknown> =>
  Object.entries(collection).reduce<Record<string, unknown>>(
    (accumulator, [templateId, item]) => {
      accumulator[templateId] =
        isRecord(item) && Object.prototype.hasOwnProperty.call(item, "data")
          ? item.data
          : item;

      return accumulator;
    },
    {},
  );

export const normalizeDashboardWidgetDataMap = (
  response: unknown,
): Record<string, unknown> => {
  if (Array.isArray(response)) {
    return normalizeWidgetDataCollection(response);
  }

  if (!isRecord(response)) {
    return {};
  }

  if (Array.isArray(response.widgets)) {
    return normalizeWidgetDataCollection(response.widgets);
  }

  if (Array.isArray(response.data)) {
    return normalizeWidgetDataCollection(response.data);
  }

  if (isRecord(response.widgets)) {
    return normalizeDashboardWidgetDataMap(response.widgets);
  }

  if (isRecord(response.data)) {
    return normalizeDashboardWidgetDataMap(response.data);
  }

  return normalizeWidgetDataRecord(response);
};

type LayoutPresetId =
  | "single_12"
  | "single_6"
  | "split_4_8"
  | "split_8_4"
  | "thirds_4";

type LayoutPresetValue = LayoutPresetId | "";

const LAYOUT_PRESETS: Array<{
  id: LayoutPresetId;
  label: string;
  widths: number[];
  icon: string;
}> = [
  // Widths are in grid columns out of BUILDER_GRID_COLS (24 — see
  // GRID_COLS.lg in src/constants/dashboard.ts), NOT the classic 12-col
  // Bootstrap convention the `id`s' names still reference. They previously
  // summed to 12, so every preset only ever filled the left half of the
  // actual 24-column grid, leaving the right half empty. `id`s are kept
  // as-is (not renamed to match) since they're persisted in saved dashboard
  // layouts (`widget.layout`) — renaming would silently stop matching an
  // already-saved preset and fall back to free-style.
  {
    id: "single_12",
    label: "One Column",
    widths: [24],
    icon: "bi-square",
  },
  {
    id: "single_6",
    label: "Two Columns",
    widths: [12, 12],
    icon: "bi-square-half",
  },
  {
    id: "split_4_8",
    label: "Left Sidebar",
    widths: [8, 16],
    icon: "bi-layout-sidebar",
  },
  {
    id: "split_8_4",
    label: "Right Sidebar",
    widths: [16, 8],
    icon: "bi-layout-sidebar-reverse",
  },
  {
    id: "thirds_4",
    label: "Three Columns",
    widths: [8, 8, 8],
    icon: "bi-layout-three-columns",
  },
];

const normalizeLayoutPresetValue = (value: unknown): LayoutPresetValue => {
  if (typeof value !== "string") {
    return "";
  }

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    !normalizedValue ||
    normalizedValue === FREE_STYLE_LAYOUT_KEY ||
    normalizedValue === "custom"
  ) {
    return "";
  }

  const matchedPreset = LAYOUT_PRESETS.find((preset) => {
    const normalizedPresetLabel = preset.label
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    return (
      preset.id === normalizedValue || normalizedPresetLabel === normalizedValue
    );
  });

  return matchedPreset?.id ?? "";
};

const buildPresetLayout = (
  layoutItems: Layout,
  presetId: LayoutPresetId,
): LayoutItem[] => {
  const preset = LAYOUT_PRESETS.find((item) => item.id === presetId);

  if (!preset) {
    return layoutItems.map((item) => ({ ...item }));
  }

  const sortedItems = [...layoutItems].sort(sortLayoutItems);
  const chartTypeByItemId = new Map(
    useBuilderStore
      .getState()
      .canvasItems.map((canvasItem) => [canvasItem.i, canvasItem.category]),
  );
  const nextLayout: LayoutItem[] = [];
  let itemIndex = 0;
  let currentY = 0;

  while (itemIndex < sortedItems.length) {
    let currentX = 0;
    let rowMaxHeight = 1;
    let presetIndex = 0;

    while (
      presetIndex < preset.widths.length &&
      itemIndex < sortedItems.length
    ) {
      const item = sortedItems[itemIndex];
      const remainingCols = BUILDER_GRID_COLS - currentX;
      const minWidth = 1;
      const maxWidth = remainingCols;
      const desiredWidth = preset.widths[presetIndex];
      const nextWidth = Math.max(
        minWidth,
        Math.min(desiredWidth ?? 0, maxWidth),
      );
      const layoutItemWithChartType = item as LayoutItem & {
        category?: string;
        chartType?: string;
        chart_type?: string;
      };
      const rawChartType =
        chartTypeByItemId.get(item?.i ?? "") ??
        layoutItemWithChartType.category ??
        layoutItemWithChartType.chartType ??
        layoutItemWithChartType.chart_type ??
        "";
      const gridConfig = getWidgetGridConfigByChartType(rawChartType);
      const nextHeight = gridConfig.defaultGridH;

      nextLayout.push({
        ...item,
        h: nextHeight,
        i: item?.i ?? String(itemIndex),
        x: currentX,
        y: currentY,
        w: nextWidth,
        minW: 1,
        maxW: BUILDER_GRID_COLS,
      });

      currentX += nextWidth;
      rowMaxHeight = Math.max(rowMaxHeight, nextHeight);
      itemIndex += 1;
      presetIndex += 1;
    }

    currentY += rowMaxHeight;
  }

  return nextLayout;
};

const applyPresetWidthsWithoutRearrange = (
  layoutItems: Layout,
  presetId: LayoutPresetId,
  sourcePresetId: LayoutPresetValue = "",
): LayoutItem[] => {
  const targetPresetColumns = getPresetColumnRanges(presetId);

  if (targetPresetColumns.length === 0) {
    return layoutItems.map((item) => ({ ...item }));
  }

  const sourcePresetColumns =
    sourcePresetId && sourcePresetId !== presetId
      ? getPresetColumnRanges(sourcePresetId)
      : [];
  const referenceColumns =
    sourcePresetColumns.length > 0 ? sourcePresetColumns : targetPresetColumns;

  return layoutItems
    .map((item) => {
      const sourceColumnIndex = getPresetColumnIndexForItem(
        item,
        referenceColumns,
      );
      const fallbackColumnIndex = getPresetColumnIndexForItem(
        item,
        targetPresetColumns,
      );
      const resolvedColumnIndex =
        sourceColumnIndex === null ? fallbackColumnIndex : sourceColumnIndex;
      const clampedColumnIndex =
        resolvedColumnIndex === null
          ? null
          : Math.max(
              0,
              Math.min(targetPresetColumns.length - 1, resolvedColumnIndex),
            );
      const targetColumn =
        clampedColumnIndex === null
          ? null
          : targetPresetColumns[clampedColumnIndex];

      if (!targetColumn) {
        return {
          ...item,
          minW: 1,
          maxW: BUILDER_GRID_COLS,
        };
      }

      return {
        ...item,
        x: targetColumn.x,
        w: targetColumn.w,
        minW: 1,
        maxW: BUILDER_GRID_COLS,
      };
    })
    .sort(sortLayoutItems);
};

const getPresetWidths = (presetId: LayoutPresetId): number[] =>
  LAYOUT_PRESETS.find((preset) => preset.id === presetId)?.widths ?? [
    BUILDER_GRID_COLS,
  ];

const buildPresetPreviewRows = (presetId: LayoutPresetId): number[][] => {
  const widths = getPresetWidths(presetId);
  const minimumPreviewSlots = 6;
  const previewRowCount = Math.max(
    1,
    Math.ceil(minimumPreviewSlots / Math.max(widths.length, 1)),
  );

  return Array.from({ length: previewRowCount }, () => widths);
};

const PRESET_PLACEHOLDER_TEMPLATE: BuilderCardTemplate = {
  id: "preset-placeholder",
  label: "Empty Slot",
  category: "Empty Slot",
  description: "Add or drag a widget here.",
  grid: {
    w: 6,
    h: 3,
    minW: 1,
    minH: 2,
    maxW: BUILDER_GRID_COLS,
    maxH: 12,
  },
};

type PresetColumnRange = {
  x: number;
  w: number;
};

const getPresetColumnRanges = (
  presetId: LayoutPresetId,
): PresetColumnRange[] => {
  const rawPresetWidths = getPresetWidths(presetId);
  const presetWidths: number[] = [];
  let occupiedCols = 0;

  rawPresetWidths.forEach((rawWidth) => {
    const remainingCols = BUILDER_GRID_COLS - occupiedCols;

    if (remainingCols <= 0) {
      return;
    }

    const normalizedWidth = Math.max(1, Math.min(rawWidth, remainingCols));
    presetWidths.push(normalizedWidth);
    occupiedCols += normalizedWidth;
  });

  let currentX = 0;

  return presetWidths.map((width) => {
    const nextRange = { x: currentX, w: width };
    currentX += width;
    return nextRange;
  });
};

const getPresetColumnIndexForItem = (
  item: Pick<LayoutItem, "x" | "w">,
  presetColumns: PresetColumnRange[],
): number | null => {
  if (presetColumns.length === 0) {
    return null;
  }

  const itemCenterX = item.x + item.w / 2;
  const directMatchIndex = presetColumns.findIndex((column, columnIndex) => {
    const columnStart = column.x;
    const columnEnd = column.x + column.w;
    const isLastColumn = columnIndex === presetColumns.length - 1;

    if (isLastColumn) {
      return itemCenterX >= columnStart && itemCenterX <= columnEnd;
    }

    return itemCenterX >= columnStart && itemCenterX < columnEnd;
  });

  if (directMatchIndex >= 0) {
    return directMatchIndex;
  }

  return presetColumns.reduce<number>(
    (closestColumnIndex, column, columnIndex) => {
      const closestColumn = presetColumns[closestColumnIndex];
      const closestDistance = Math.abs(
        itemCenterX - (closestColumn?.x ?? 0 + (closestColumn?.w ?? 0) / 2),
      );
      const columnDistance = Math.abs(itemCenterX - (column.x + column.w / 2));

      return columnDistance < closestDistance
        ? columnIndex
        : closestColumnIndex;
    },
    0,
  );
};

const getPresetColumnWidgetCounts = (
  layoutItems: Array<Pick<LayoutItem, "x" | "w">>,
  presetColumns: PresetColumnRange[],
): number[] => {
  const widgetCounts = Array.from({ length: presetColumns.length }, () => 0);

  layoutItems.forEach((item) => {
    const columnIndex = getPresetColumnIndexForItem(item, presetColumns);

    if (columnIndex === null) {
      return;
    }

    widgetCounts[columnIndex] = (widgetCounts[columnIndex] ?? 0) + 1;
  });

  return widgetCounts;
};

const getEmptyPresetColumnIndexes = (widgetsPerColumn: number[]): number[] =>
  widgetsPerColumn.reduce<number[]>(
    (emptyColumnIndexes, widgetCount, columnIndex) => {
      if (widgetCount === 0) {
        emptyColumnIndexes.push(columnIndex);
      }

      return emptyColumnIndexes;
    },
    [],
  );

const getRequiredPresetPlaceholderCount = (
  emptyPresetColumnIndexes: number[],
  assignedItemCount: number,
): number => {
  if (assignedItemCount === 0) {
    return 0;
  }

  return emptyPresetColumnIndexes.length;
};

export const toSaveLayoutWidgets = (
  items: ReturnType<
    typeof useBuilderStore.getState
  >["saveLayoutDraft"]["items"],
) =>
  items
    .filter((item) => item.assigned_widget_id !== null)
    .map((item: (typeof items)[number]) => ({
      grid_h: item.grid.h,
      grid_w: item.grid.w,
      grid_x: item.grid.x,
      grid_y: item.grid.y,
      position: item.position,
      is_visible: item.is_visible,
      // `chart_type` is deliberately NOT sent. It isn't part of
      // LayoutWidgetItem (the endpoint derives the real type from
      // widget_template_id), and sending it was actively harmful: it came
      // from item.category, which is seeded off the API's widget.chart_type,
      // so once a response came back with chart_type "table" the builder
      // echoed "table" back on every save and the widget permanently lost
      // its real type ("pie"). The source chart type belongs to the
      // template; only the *view* to open in is ours to send, below.
      //
      // Always sent, empty object included: clearing a setting has to
      // overwrite what is stored, not be omitted from the payload.
      //
      // The widget row's `id` is also deliberately omitted, even though
      // LayoutWidgetItem declares it — the endpoint documents that widgets
      // are "upserted by widget_template_id", which is the contract that has
      // always worked here.
      config_override: toConfigOverride(item.config),
      widget_template_id: Number(item.assigned_widget_id),
    }));

export default function DashboardBuilder(): JSX.Element {
  const navigate = useNavigate();
  const { dashboard_name } = useParams<{ dashboard_name: string }>();
  const { user: userData } = useAuthStore();
  const isPortalSession = usePortalAuthStore((s) => s.isPortalSession);
  const { data: portalMe } = usePortalMe(isPortalSession);
  const isSuperAdmin = portalMe?.role === "super_admin";
  const { width, containerRef, mounted } = useContainerWidth({
    initialWidth: 960,
  });
  const pageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const layoutPresetApplyFrameRef = useRef<number | null>(null);
  const addedHighlightTimeoutRef = useRef<number | null>(null);
  const canvasItems = useBuilderStore((state) => state.canvasItems);
  const currentDashboard = useBuilderStore((state) => state.currentDashboard);
  const addWidgetCanvasItem = useBuilderStore(
    (state) => state.addWidgetCanvasItem,
  );
  const addCanvasItem = useBuilderStore((state) => state.addCanvasItem);
  const removeCanvasItem = useBuilderStore((state) => state.removeCanvasItem);
  const swapCanvasItems = useBuilderStore((state) => state.swapCanvasItems);
  const assignWidgetToCanvasItem = useBuilderStore(
    (state) => state.assignWidgetToCanvasItem,
  );
  const commitCanvasLayout = useBuilderStore(
    (state) => state.commitCanvasLayout,
  );
  const saveLayoutDraft = useBuilderStore((state) => state.saveLayoutDraft);
  const pendingWidgetDeletionIds = useBuilderStore(
    (state) => state.pendingWidgetDeletionIds,
  );
  const clearPendingWidgetDeletions = useBuilderStore(
    (state) => state.clearPendingWidgetDeletions,
  );
  const selectedDashboardCode = useBuilderStore(
    (state) => state.selectedDashboardCode,
  );
  const setSelectedDashboardCode = useBuilderStore(
    (state) => state.setSelectedDashboardCode,
  );
  const setDashboardTemplates = useBuilderStore(
    (state) => state.setDashboardTemplates,
  );
  const resetBuilderStore = useBuilderStore((state) => state.resetBuilderStore);
  const queryClient = useQueryClient();

  const { data: templatesResponse } = useQuery({
    queryKey: ["dashboard_templates_all"],
    queryFn: () => getDashboardTemplatesApi<DashboardTemplatesResponse>(),
    enabled: !!selectedDashboardCode,
  });

  const [activeCanvasInteractionItemId, setActiveCanvasInteractionItemId] =
    useState<string | null>(null);
  const [canvasResetKey, setCanvasResetKey] = useState<number>(0);
  const [isCreateDashboardModalOpen, setIsCreateDashboardModalOpen] =
    useState(false);
  const [isLayoutPresetMenuOpen, setIsLayoutPresetMenuOpen] = useState(false);
  const [selectedLayoutPreset, setSelectedLayoutPreset] =
    useState<LayoutPresetValue>("");
  const [draftDashboardTitle, setDraftDashboardTitle] = useState<string | null>(
    null,
  );
  const [unsavedWidgetItemIds, setUnsavedWidgetItemIds] = useState<
    Record<string, true>
  >({});
  const [isUnsavedWidgetsModalOpen, setIsUnsavedWidgetsModalOpen] =
    useState(false);
  const [recentlyAddedCanvasItemId, setRecentlyAddedCanvasItemId] = useState<
    string | null
  >(null);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled: !!userData,
    staleTime: 0,
  });

  const isReadOnly = useMemo(() => {
    if (!selectedDashboardCode || !allDashboards) return false;
    const found = allDashboards.dashboards.find(
      (d) => d.code === selectedDashboardCode,
    );
    return found?.read_only === true;
  }, [selectedDashboardCode, allDashboards]);

  const canEdit = !isReadOnly || isSuperAdmin;

  const { mutate: saveAsDashboard, isPending: isSavingAs } = useMutation({
    mutationFn: (name: string) => {
      if (!selectedDashboardCode) {
        throw new Error("No dashboard selected.");
      }
      return saveAsDashboardApi(selectedDashboardCode, {
        name: name || undefined,
      });
    },
    onSuccess: async (data: unknown) => {
      setIsSaveAsModalOpen(false);
      setSaveAsName("");
      await queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      const result = data as
        | {
            dashboard?: { code?: string; name?: string };
          }
        | undefined;
      const newCode = result?.dashboard?.code;
      if (newCode) {
        resetBuilderStore();
        setSelectedDashboardCode(newCode);
        navigate(`/builder/${newCode}`);
      }
      toast.success("Dashboard copy created successfully.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create dashboard copy.");
    },
  });

  const layout: Layout = canvasItems.map(
    ({ templateId, label, category, description, ...item }) => {
      const gridConfig = getWidgetGridConfigByChartType(category);
      return {
        ...item,
        minW: gridConfig.minGridW,
        minH: gridConfig.minGridH,
        maxW: gridConfig.maxGridW,
        maxH: gridConfig.maxGridH,
      };
    },
  );

  // Below `md` the canvas shows a REFLOWED view of the layout, not the layout
  // itself. Editing is switched off there (see isAuthoringWidth) so the
  // reflow can never be committed: commitCanvasLayout only ever runs from the
  // drag/drop/resize handlers, so with those disabled the stored layout keeps
  // exactly the arrangement that was authored on a wide screen.
  const builderBreakpoint = useMemo(
    () => getBreakpointFromWidth(GRID_BREAKPOINTS, width),
    [width],
  );
  const isAuthoringWidth = (GRID_MIN_WIDTHS[
    builderBreakpoint as keyof typeof GRID_MIN_WIDTHS
  ] ?? 1) <= 1;
  const canvasLayouts = useMemo(
    () => buildResponsiveLayoutsFromLayout(layout),
    [layout],
  );
  const canEditLayout = canEdit && isAuthoringWidth;

  useEffect(() => {
    if (!selectedDashboardCode) {
      setDashboardTemplates([]);
      return;
    }

    const tiers = templatesResponse?.tiers ?? [];
    setDashboardTemplates(flattenDashboardTemplates(tiers));
  }, [selectedDashboardCode, templatesResponse, setDashboardTemplates]);

  useEffect(() => {
    if (currentDashboard?.dashboard.code === selectedDashboardCode) {
      setDraftDashboardTitle(null);
    }
  }, [currentDashboard, selectedDashboardCode]);

  useEffect(() => {
    if (dashboard_name) {
      setSelectedDashboardCode(decodeURIComponent(dashboard_name));
    }
  }, [dashboard_name, setSelectedDashboardCode]);

  // Time spent EDITING this dashboard, reported as builder.opened / builder.dwell.
  //
  // Kept distinct from viewing it: `GET /dashboards/{code}` is called by both this
  // page and DashboardPage, so the server cannot tell them apart — but they are
  // separate routes here, so each declares its own mode. Without this, builder time
  // was attributed to no dashboard at all.
  useEffect(() => {
    if (!dashboard_name) return;
    const visit = startBuilderVisit(decodeURIComponent(dashboard_name));
    return () => visit.end();
  }, [dashboard_name]);

  useEffect(() => {
    setSelectedLayoutPreset("");
    setIsLayoutPresetMenuOpen(false);
  }, [selectedDashboardCode]);

  useEffect(() => {
    const persistedLayoutValue =
      currentDashboard?.dashboard?.layout ?? currentDashboard?.layout;
    const nextPreset = normalizeLayoutPresetValue(persistedLayoutValue);

    setSelectedLayoutPreset(nextPreset);
    setIsLayoutPresetMenuOpen(false);
  }, [currentDashboard]);

  const handleChangeLayoutPreset = (presetId: LayoutPresetValue) => {
    const isPresetChanged = presetId !== selectedLayoutPreset;
    setSelectedLayoutPreset(presetId);
    setIsLayoutPresetMenuOpen(false);

    if (layoutPresetApplyFrameRef.current !== null) {
      window.cancelAnimationFrame(layoutPresetApplyFrameRef.current);
      layoutPresetApplyFrameRef.current = null;
    }

    if (!isPresetChanged) {
      return;
    }

    if (!presetId) {
      return;
    }

    const latestLayout: Layout = useBuilderStore
      .getState()
      .canvasItems.map(
        ({ templateId, label, category, description, ...item }) => item,
      );

    if (latestLayout.length === 0) {
      return;
    }

    commitCanvasLayout(
      applyPresetWidthsWithoutRearrange(
        latestLayout,
        presetId,
        selectedLayoutPreset,
      ),
    );
  };

  useEffect(() => {
    const placeholderItems = canvasItems
      .filter((item) => item.assignedWidgetId === null)
      .sort(sortLayoutItems);
    const assignedItems = canvasItems
      .filter((item) => item.assignedWidgetId !== null)
      .sort(sortLayoutItems);

    if (!selectedLayoutPreset) {
      const trailingPlaceholder = placeholderItems[placeholderItems.length - 1];

      if (trailingPlaceholder) {
        removeCanvasItem(trailingPlaceholder.i);
      }

      return;
    }

    const presetColumns = getPresetColumnRanges(selectedLayoutPreset);
    const assignedItemCount = assignedItems.length;
    const widgetsPerPresetColumn = getPresetColumnWidgetCounts(
      assignedItems,
      presetColumns,
    );
    const emptyPresetColumnIndexes = getEmptyPresetColumnIndexes(
      widgetsPerPresetColumn,
    );
    const requiredPlaceholderCount = getRequiredPresetPlaceholderCount(
      emptyPresetColumnIndexes,
      assignedItemCount,
    );

    if (placeholderItems.length < requiredPlaceholderCount) {
      addCanvasItem(PRESET_PLACEHOLDER_TEMPLATE);
      return;
    }

    if (placeholderItems.length > requiredPlaceholderCount) {
      const trailingPlaceholder = placeholderItems[placeholderItems.length - 1];

      if (trailingPlaceholder) {
        removeCanvasItem(trailingPlaceholder.i);
      }

      return;
    }

    const nextLayout: LayoutItem[] = canvasItems.map(
      ({ templateId, label, category, description, ...item }) => ({
        ...item,
      }),
    );
    const layoutById = new Map(
      nextLayout.map((layoutItem) => [layoutItem.i, layoutItem]),
    );
    let hasPlaceholderPositionChanges = false;

    placeholderItems.forEach((placeholderItem, placeholderIndex) => {
      const columnIndex = emptyPresetColumnIndexes[placeholderIndex];
      if (!columnIndex) return;
      const targetColumn = presetColumns[columnIndex];
      const targetLayoutItem = layoutById.get(placeholderItem.i);

      if (!targetLayoutItem || !targetColumn) {
        return;
      }

      const nextPlaceholderY = 0;
      const hasLayoutChanges =
        targetLayoutItem.x !== targetColumn.x ||
        targetLayoutItem.y !== nextPlaceholderY ||
        targetLayoutItem.w !== targetColumn.w ||
        targetLayoutItem.minW !== 1 ||
        targetLayoutItem.maxW !== BUILDER_GRID_COLS;

      if (!hasLayoutChanges) {
        return;
      }

      hasPlaceholderPositionChanges = true;
      layoutById.set(placeholderItem.i, {
        ...targetLayoutItem,
        x: targetColumn.x,
        y: nextPlaceholderY,
        w: targetColumn.w,
        minW: 1,
        maxW: BUILDER_GRID_COLS,
      });
    });

    if (!hasPlaceholderPositionChanges) {
      return;
    }

    commitCanvasLayout(Array.from(layoutById.values()).sort(sortLayoutItems));
  }, [
    addCanvasItem,
    canvasItems,
    commitCanvasLayout,
    removeCanvasItem,
    selectedLayoutPreset,
  ]);

  const { mutate: saveLayout, isPending: isSavingLayout } = useMutation({
    mutationFn: async () => {
      if (!selectedDashboardCode) {
        throw new Error("No dashboard is selected.");
      }

      const unsavedItemIdSet = new Set(Object.keys(unsavedWidgetItemIds));
      const widgetsToSave = saveLayoutDraft.items.filter(
        (item) =>
          item.assigned_widget_id !== null &&
          !unsavedItemIdSet.has(item.item_id),
      );

      const updatePromise = updateDashboardLayoutApi(selectedDashboardCode, {
        layout: selectedLayoutPreset || FREE_STYLE_LAYOUT_KEY,
        widgets: toSaveLayoutWidgets(widgetsToSave),
      });
      const deletionIds = Array.from(new Set(pendingWidgetDeletionIds));
      const deletePromise = deletionIds.length
        ? Promise.allSettled(
            deletionIds.map((widgetId) =>
              deleteDashboardWidgetApi(selectedDashboardCode, widgetId),
            ),
          )
        : Promise.resolve([] as PromiseSettledResult<unknown>[]);

      const [updateResponse, deletionResults] = await Promise.all([
        updatePromise,
        deletePromise,
      ]);

      const failedDeletes = deletionResults.filter(
        (result) => result.status === "rejected",
      );

      if (failedDeletes.length > 0) {
        throw new Error(
          `Failed to delete ${failedDeletes.length} widget${failedDeletes.length === 1 ? "" : "s"}.`,
        );
      }

      return updateResponse;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["dashboard_name", selectedDashboardCode],
        }),
        queryClient.invalidateQueries({
          queryKey: ["dashboard_templates", selectedDashboardCode],
        }),
        queryClient.invalidateQueries({
          queryKey: ["builder_dashboard_data", selectedDashboardCode],
        }),
        queryClient.invalidateQueries({
          queryKey: ["individual_widget_data", selectedDashboardCode],
        }),
      ]);
      clearPendingWidgetDeletions();
      toast.success("Layout saved successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save layout");
    },
  });

  // Wrapped in useCallback because handleSlotDrop below lists it as a
  // dependency — as a plain function it was re-created on every render, so that
  // useCallback (and every handler derived from it) was re-created too.
  const addWidgetToCanvas = useCallback(
    (widget: BuilderWidgetTemplate) => {
      const nextCanvasState = useBuilderStore.getState();
      const firstPlaceholderItem = selectedLayoutPreset
        ? [...nextCanvasState.canvasItems]
            .filter((item) => item.assignedWidgetId === null)
            .sort(sortLayoutItems)[0]
        : null;
      const nextCanvasItemId = nextCanvasState.nextCanvasItemId;
      const nextItemId =
        firstPlaceholderItem?.i ?? `builder-card-${nextCanvasItemId}`;

      if (firstPlaceholderItem) {
        assignWidgetToCanvasItem(firstPlaceholderItem.i, widget.id);
      } else {
        addWidgetCanvasItem(widget.id);
      }

      setRecentlyAddedCanvasItemId(nextItemId);

      if (addedHighlightTimeoutRef.current !== null) {
        window.clearTimeout(addedHighlightTimeoutRef.current);
      }
      addedHighlightTimeoutRef.current = window.setTimeout(() => {
        setRecentlyAddedCanvasItemId((currentItemId) =>
          currentItemId === nextItemId ? null : currentItemId,
        );
        addedHighlightTimeoutRef.current = null;
      }, 1800);

      const scrollToCanvasItemWhenReady = (
        itemId: string,
        remainingFrames: number = 24,
      ) => {
        const scrollContainer = canvasRef.current;
        const itemElement = document.getElementById(`builder-item-${itemId}`);

        if (!scrollContainer || !itemElement) {
          if (remainingFrames <= 0) {
            return;
          }

          window.requestAnimationFrame(() => {
            scrollToCanvasItemWhenReady(itemId, remainingFrames - 1);
          });
          return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const itemRect = itemElement.getBoundingClientRect();
        const itemBottomWithinContainer =
          itemRect.bottom - containerRect.top + scrollContainer.scrollTop;
        const targetTop = Math.max(
          0,
          itemBottomWithinContainer - scrollContainer.clientHeight + 100,
        );

        scrollContainer.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });
      };

      window.requestAnimationFrame(() => {
        scrollToCanvasItemWhenReady(nextItemId);
      });

      if (selectedLayoutPreset) {
        window.requestAnimationFrame(() => {
          const latestLayout: Layout = useBuilderStore
            .getState()
            .canvasItems.map(
              ({ templateId, label, category, description, ...item }) => item,
            );

          commitCanvasLayout(
            buildPresetLayout(latestLayout, selectedLayoutPreset),
          );
        });
      }
      // sortLayoutItems / buildPresetLayout are module-scope, and the refs plus
      // the useState setter are stable, so only these four need listing.
    },
    [
      selectedLayoutPreset,
      assignWidgetToCanvasItem,
      addWidgetCanvasItem,
      commitCanvasLayout,
    ],
  );

  const [dropTargetItemId, setDropTargetItemId] = useState<string | null>(null);

  const parseWidgetFromDrag = useCallback(
    (event: DragEvent<HTMLElement>): BuilderWidgetTemplate | null => {
      const raw = event.dataTransfer.getData(BUILDER_WIDGET_DRAG_TYPE);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as BuilderWidgetTemplate;
      } catch {
        return null;
      }
    },
    [],
  );

  const handleSlotDragOver = useCallback(
    (event: DragEvent<HTMLElement>, itemId: string) => {
      if (!event.dataTransfer.types.includes(BUILDER_WIDGET_DRAG_TYPE)) return;
      const item = canvasItems.find((ci) => ci.i === itemId);
      if (item && item.assignedWidgetId !== null) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setDropTargetItemId(itemId);
    },
    [canvasItems],
  );

  const handleSlotDrop = useCallback(
    (event: DragEvent<HTMLElement>, itemId: string) => {
      event.preventDefault();
      setDropTargetItemId(null);
      const widget = parseWidgetFromDrag(event);
      if (!widget) return;
      const item = canvasItems.find((ci) => ci.i === itemId);
      if (item && item.assignedWidgetId === null) {
        assignWidgetToCanvasItem(itemId, widget.id);
        setRecentlyAddedCanvasItemId(itemId);
        if (addedHighlightTimeoutRef.current !== null) {
          window.clearTimeout(addedHighlightTimeoutRef.current);
        }
        addedHighlightTimeoutRef.current = window.setTimeout(() => {
          setRecentlyAddedCanvasItemId((cur) => (cur === itemId ? null : cur));
          addedHighlightTimeoutRef.current = null;
        }, 1800);
      } else {
        addWidgetToCanvas(widget);
      }
    },
    [
      canvasItems,
      assignWidgetToCanvasItem,
      parseWidgetFromDrag,
      addWidgetToCanvas,
    ],
  );

  const handleSlotDragLeave = useCallback((itemId: string) => {
    setDropTargetItemId((current) => (current === itemId ? null : current));
  }, []);

  const handleCanvasDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes(BUILDER_WIDGET_DRAG_TYPE)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    [],
  );

  const getGridPositionFromDrop = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
    ): { x: number; y: number } | undefined => {
      const gridEl = containerRef.current;
      if (!gridEl) return undefined;
      const rect = gridEl.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      const colWidth =
        (rect.width - GRID_MARGIN[0] * (BUILDER_GRID_COLS - 1)) /
        BUILDER_GRID_COLS;
      const cellWidth = colWidth + GRID_MARGIN[0];
      const cellHeight = BUILDER_ROW_HEIGHT + GRID_MARGIN[1];
      const gridX = Math.max(
        0,
        Math.min(BUILDER_GRID_COLS - 1, Math.floor(offsetX / cellWidth)),
      );
      const gridY = Math.max(0, Math.floor(offsetY / cellHeight));
      return { x: gridX, y: gridY };
    },
    [containerRef],
  );

  const handleCanvasDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (dropTargetItemId) return;
      event.preventDefault();
      const widget = parseWidgetFromDrag(event);
      if (!widget) return;
      const pos = getGridPositionFromDrop(event);
      addWidgetCanvasItem(widget.id, pos);

      const nextItemId = `builder-card-${useBuilderStore.getState().nextCanvasItemId - 1}`;
      setRecentlyAddedCanvasItemId(nextItemId);
      if (addedHighlightTimeoutRef.current !== null) {
        window.clearTimeout(addedHighlightTimeoutRef.current);
      }
      addedHighlightTimeoutRef.current = window.setTimeout(() => {
        setRecentlyAddedCanvasItemId((cur) =>
          cur === nextItemId ? null : cur,
        );
        addedHighlightTimeoutRef.current = null;
      }, 1800);
    },
    [
      dropTargetItemId,
      parseWidgetFromDrag,
      getGridPositionFromDrop,
      addWidgetCanvasItem,
    ],
  );

  const handleSaveLayout = () => {
    const unsavedWidgetCount = Object.keys(unsavedWidgetItemIds).length;

    if (unsavedWidgetCount > 0) {
      setIsUnsavedWidgetsModalOpen(true);
      return;
    }

    saveLayout();
  };

  const handleCloseUnsavedWidgetsModal = () => {
    if (isSavingLayout) {
      return;
    }

    setIsUnsavedWidgetsModalOpen(false);
  };

  const handleConfirmSaveIgnoringUnsavedWidgets = () => {
    setIsUnsavedWidgetsModalOpen(false);
    saveLayout();
  };

  const handleAddNewDashboard = () => {
    setIsCreateDashboardModalOpen(true);
  };

  const handleCreateDashboard = (dashboard: {
    code: string;
    name: string;
    user_types: string[];
  }) => {
    resetBuilderStore();
    setSelectedLayoutPreset("");
    setUnsavedWidgetItemIds({});
    setCanvasResetKey((currentValue) => currentValue + 1);
    setDraftDashboardTitle(dashboard.name);
    setSelectedDashboardCode(dashboard.code);
    setIsCreateDashboardModalOpen(false);
    // Keep the URL in sync so a refresh reopens this dashboard instead of
    // snapping back to whatever /builder/:dashboard_name was before creation.
    navigate(`/builder/${dashboard.code}`);
  };

  const gridInteractionProps = useBuilderGridInteractions({
    pageRef,
    selectedLayoutPreset,
    commitCanvasLayout,
    swapCanvasItems,
    buildPresetLayout: (layoutItems, presetId) =>
      buildPresetLayout(layoutItems, presetId as LayoutPresetId),
    setActiveCanvasInteractionItemId,
  });

  useEffect(() => {
    if (layoutPresetApplyFrameRef.current !== null) {
      window.cancelAnimationFrame(layoutPresetApplyFrameRef.current);
      layoutPresetApplyFrameRef.current = null;
    }

    if (addedHighlightTimeoutRef.current !== null) {
      window.clearTimeout(addedHighlightTimeoutRef.current);
      addedHighlightTimeoutRef.current = null;
    }

    return () => {
      resetBuilderStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={pageRef}
      className="container-fluid px-4 px-lg-5 py-3 d-flex flex-column flex-fill"
    >
      <SubHeader
        onAddNewDashboard={handleAddNewDashboard}
        onSaveLayout={handleSaveLayout}
        isSavingLayout={isSavingLayout}
        dashboardTitleOverride={draftDashboardTitle}
        isReadOnly={isReadOnly}
        onCreateCopy={() => {
          setSaveAsName(
            currentDashboard?.dashboard?.name
              ? `${currentDashboard.dashboard.name} (Copy)`
              : "",
          );
          setIsSaveAsModalOpen(true);
        }}
        onDashboardCodeChange={(newCode) => {
          setSelectedDashboardCode(newCode);
          navigate(`/builder/${newCode}`, { replace: true });
        }}
      />

      {/* `builder-workspace` is what the stacking media query targets; the
          Bootstrap utilities stay because their !important gap/display
          still win, so the desktop layout is unchanged. */}
      <div className="builder-workspace d-flex mt-4 flex-fill gap-4">
        {canEdit && (
            <Sidebar onAddWidget={addWidgetToCanvas} />
        )}
        <div ref={canvasRef} className="builder-workspace__canvas">
          <div className="builder-canvas-panel">
            <div className="builder-canvas-panel__header">
              <div>
                <span className="builder-eyebrow">
                  {canEdit ? "Canvas" : " "}
                </span>
                <h2 className="builder-canvas-panel__title mb-2">
                  {canEdit ? "Draggable and resizable GridLayout" : "Read Only"}
                </h2>
                <p className="builder-canvas-panel__copy mb-0">
                  {canEdit
                    ? "Click or drag any widget from the sidebar to add it to the canvas, then reposition or resize it inside the layout."
                    : 'This is a shared dashboard and cannot be edited directly. To make changes, click "Save As" to create your own editable copy.'}
                </p>
              </div>

              {canEdit && (
                <div className="builder-canvas-panel__actions">
                  <div className="position-relative">
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        isLayoutPresetMenuOpen
                          ? "btn-outline-primary"
                          : "btn-primary"
                      }`}
                      onClick={() => setIsLayoutPresetMenuOpen((open) => !open)}
                    >
                      <i
                        className={`bi me-2 ${LAYOUT_PRESETS.find((preset) => preset.id === selectedLayoutPreset)?.icon || "bi-sliders"}`}
                      />
                      {selectedLayoutPreset
                        ? ` ${LAYOUT_PRESETS.find((preset) => preset.id === selectedLayoutPreset)?.label}`
                        : "Free Style "}
                    </button>

                    {isLayoutPresetMenuOpen && (
                      <div
                        className="bg-white border rounded shadow-sm p-2 position-absolute end-0 mt-2"
                        style={{ width: "200px", zIndex: 20 }}
                      >
                        <div className="small text-uppercase text-muted fw-semibold px-1 pb-2">
                          Layout Presets
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <button
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-2 text-start ${
                              selectedLayoutPreset === ""
                                ? "btn-primary"
                                : "btn-light border"
                            }`}
                            onClick={() => handleChangeLayoutPreset("")}
                          >
                            <i className="bi bi-sliders" />
                            <span>Free Style </span>
                          </button>
                          {LAYOUT_PRESETS.map((preset) => (
                            <button
                              key={preset.id}
                              type="button"
                              className={`btn btn-sm d-flex align-items-center gap-2 text-start ${
                                selectedLayoutPreset === preset.id
                                  ? "btn-primary"
                                  : "btn-light border"
                              }`}
                              onClick={() =>
                                handleChangeLayoutPreset(preset.id)
                              }
                            >
                              <i className={`bi ${preset.icon}`} />
                              <span>{preset.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className={`${
                canvasItems.length === 0 && selectedLayoutPreset
                  ? "builder-canvas-grid--layout-preview"
                  : ""
              }`}
              ref={containerRef}
              onDragOver={canEdit ? handleCanvasDragOver : undefined}
              onDrop={canEdit ? handleCanvasDrop : undefined}
            >
              {mounted && canvasItems.length > 0 && (
                <ResponsiveGridLayout
                  key={canvasResetKey}
                  width={width}
                  layouts={canvasLayouts}
                  breakpoints={GRID_BREAKPOINTS}
                  cols={GRID_COLS}
                  className="builder-live-grid"
                  rowHeight={
                    GRID_ROW_HEIGHTS[builderBreakpoint] ?? BUILDER_ROW_HEIGHT
                  }
                  margin={GRID_MARGINS[builderBreakpoint] ?? GRID_MARGIN}
                  containerPadding={GRID_CONTAINER_PADDING}
                  compactor={verticalCompactor}
                  dragConfig={
                    canEditLayout
                      ? gridInteractionProps.dragConfig
                      : { enabled: false }
                  }
                  resizeConfig={
                    canEditLayout
                      ? gridInteractionProps.resizeConfig
                      : { enabled: false }
                  }
                  onDragStart={
                    canEditLayout ? gridInteractionProps.onDragStart : undefined
                  }
                  onDrag={canEditLayout ? gridInteractionProps.onDrag : undefined}
                  onDragStop={
                    canEditLayout ? gridInteractionProps.onDragStop : undefined
                  }
                  onResizeStart={
                    canEditLayout ? gridInteractionProps.onResizeStart : undefined
                  }
                  onResizeStop={
                    canEditLayout ? gridInteractionProps.onResizeStop : undefined
                  }
                >
                  {canvasItems.map((item) => {
                    const isRecentlyAdded =
                      recentlyAddedCanvasItemId === item.i;

                    return (
                      <div key={item.i} id={`builder-item-${item.i}`}>
                        <Slot
                          item={item}
                          className={
                            isRecentlyAdded
                              ? "builder-live-grid__item--newly-added"
                              : undefined
                          }
                          isRecentlyAdded={isRecentlyAdded}
                          isInteractionActive={
                            activeCanvasInteractionItemId === item.i
                          }
                          isDropTarget={dropTargetItemId === item.i}
                          onDragOver={handleSlotDragOver}
                          onDrop={handleSlotDrop}
                          onDragLeave={handleSlotDragLeave}
                          onRemove={
                            canEdit
                              ? (itemId) => {
                                  setUnsavedWidgetItemIds((currentItems) => {
                                    if (!currentItems[itemId]) {
                                      return currentItems;
                                    }

                                    const nextItems = { ...currentItems };
                                    delete nextItems[itemId];
                                    return nextItems;
                                  });
                                  removeCanvasItem(itemId);
                                }
                              : undefined
                          }
                          isWidgetFormInitiallyOpen={
                            unsavedWidgetItemIds[item.i] === true
                          }
                          onWidgetFormSaved={(itemId) => {
                            setUnsavedWidgetItemIds((currentItems) => {
                              if (!currentItems[itemId]) {
                                return currentItems;
                              }

                              const nextItems = { ...currentItems };
                              delete nextItems[itemId];
                              return nextItems;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </ResponsiveGridLayout>
              )}

              {mounted && canvasItems.length === 0 && selectedLayoutPreset && (
                <div className="builder-canvas-grid__empty builder-canvas-grid__empty--layout-preview">
                  <div className="builder-layout-skeleton" aria-hidden="true">
                    {buildPresetPreviewRows(selectedLayoutPreset).map(
                      (rowWidths, rowIndex) => (
                        <div
                          key={`preset-row-${selectedLayoutPreset}-${rowIndex}`}
                          className="builder-layout-skeleton__row"
                        >
                          {rowWidths.map((columnWidth, columnIndex) => (
                            <div
                              key={`preset-cell-${selectedLayoutPreset}-${rowIndex}-${columnIndex}`}
                              className="builder-layout-skeleton__cell"
                              style={{
                                width: `${(columnWidth / BUILDER_GRID_COLS) * 100}%`,
                                height: `${BUILDER_ROW_HEIGHT * 2.5}px`,
                              }}
                            >
                              <div className="builder-layout-skeleton__cell-header">
                                <span className="builder-layout-skeleton__badge" />
                                <span className="builder-layout-skeleton__kebab" />
                              </div>
                              <span className="builder-layout-skeleton__line builder-layout-skeleton__line--title" />
                              <span className="builder-layout-skeleton__line" />
                              <span className="builder-layout-skeleton__line builder-layout-skeleton__line--short" />
                            </div>
                          ))}
                        </div>
                      ),
                    )}
                  </div>

                  <h3 className="mb-2">Layout preview</h3>
                  <p className="mb-3">
                    This dashboard is empty. The skeleton shows how the selected
                    layout will look once you add slots.
                  </p>
                </div>
              )}

              {mounted && canvasItems.length === 0 && !selectedLayoutPreset && (
                <div className="builder-canvas-grid__empty">
                  <i className="bi bi-grid-1x2" />
                  <h3 className="mb-2">Canvas is empty</h3>
                  <p className="mb-3">
                    Click or drag any widget from the sidebar to add the first
                    card to the layout.
                  </p>
                </div>
              )}
            </div>

            {canEdit && (
              <div className="builder-canvas-panel__footer">
                <div className="builder-canvas-panel__hint">
                  <i className="bi bi-mouse" />
                  <span>Cards inside the canvas are draggable.</span>
                </div>
                <div className="builder-canvas-panel__hint">
                  <i className="bi bi-arrow-left-right" />
                  <span>
                    Use the southeast handle to resize within each template
                    limit.
                  </span>
                </div>
                <div className="builder-canvas-panel__hint">
                  <i className="bi bi-layout-wtf" />
                  <span>
                    The layout keeps 24 columns with dashboard spacing
                    constants.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateDashboardModal
        show={isCreateDashboardModalOpen}
        initialName={draftDashboardTitle ?? ""}
        onClose={() => setIsCreateDashboardModalOpen(false)}
        onSubmit={handleCreateDashboard}
      />

      <Modal
        show={isSaveAsModalOpen}
        centered
        onHide={() => {
          if (!isSavingAs) setIsSaveAsModalOpen(false);
        }}
      >
        <Modal.Header closeButton={!isSavingAs}>
          <Modal.Title>Save As</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will create an editable copy of this dashboard in your My
            Dashboards.
          </p>
          <label htmlFor="save-as-name" className="form-label">
            Dashboard name
          </label>
          <input
            id="save-as-name"
            type="text"
            className="form-control"
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveAsDashboard(saveAsName.trim());
              }
            }}
            placeholder="My custom dashboard"
            maxLength={255}
            disabled={isSavingAs}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setIsSaveAsModalOpen(false)}
            disabled={isSavingAs}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => saveAsDashboard(saveAsName.trim())}
            disabled={isSavingAs}
          >
            {isSavingAs ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={isUnsavedWidgetsModalOpen}
        centered
        onHide={handleCloseUnsavedWidgetsModal}
      >
        <Modal.Header closeButton={!isSavingLayout}>
          <Modal.Title>Unsaved Widgets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          There are {Object.keys(unsavedWidgetItemIds).length} unsaved widgets.
          Do you want to ignore them and continue saving the layout?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseUnsavedWidgetsModal}
            disabled={isSavingLayout}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirmSaveIgnoringUnsavedWidgets}
            disabled={isSavingLayout}
          >
            {isSavingLayout ? "Saving..." : "Ignore and Save"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
